import React from "react";
import { withTranslation } from "react-i18next";
import { Badge } from "react-bootstrap";
import AnomalyInfoTip from "../../AnomalyInfoTip";
import { SEVERITY_BADGE } from "../constants/timelineSeverity";
import { deriveHealthStatus, deriveLatestPeriodStatus, deriveStreaks } from "../utils/timelineHealth";
import {
  formatPeriodsAffected,
  formatSeverityLabel,
  formatTimelinePeriod,
  formatValue,
} from "../utils/timelineFormatters";

const HEALTH_BADGE = {
  STABLE:   "badge-Closed",
  WATCH:    "badge-Medium",
  AT_RISK:  "badge-High",
  CRITICAL: "badge-Critical",
};

const SummaryStrip = ({ t, summary, dataPoints }) => {
  const health = deriveHealthStatus(summary, dataPoints);
  const streaks = deriveStreaks(dataPoints);
  const latest = deriveLatestPeriodStatus(dataPoints);
  const totalPeriods = dataPoints.length;
  const affected = summary?.periodsWithAnomalies ?? 0;

  const healthLabelKey = {
    STABLE:   "MetricAnomalyTimeline.healthStable.label",
    WATCH:    "MetricAnomalyTimeline.healthWatch.label",
    AT_RISK:  "MetricAnomalyTimeline.healthAtRisk.label",
    CRITICAL: "MetricAnomalyTimeline.healthCritical.label",
  };

  return (
    <div className="mat-summary-strip row g-3" data-testid="mat-summary-strip">
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value">{summary?.totalAnomalies ?? 0}</div>
          <div className="mat-summary-label label-with-tip">
            {t("AnomalyDetection.totalAnomalies.label")}
            <AnomalyInfoTip text={t("AnomalyDetection.totalAnomaliesToolTip.label")} />
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value mat-summary-value-sm">
            {formatPeriodsAffected(affected, totalPeriods, t)}
          </div>
          <div className="mat-summary-label label-with-tip">
            {t("MetricAnomalyTimeline.periodsAffectedTitle.label")}
            <AnomalyInfoTip text={t("AnomalyDetection.periodsFlaggedToolTip.label")} />
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value">
            {summary?.maxSeverity ? (
              <Badge bg={SEVERITY_BADGE[summary.maxSeverity] || "secondary"}>
                {formatSeverityLabel(summary.maxSeverity, t)}
              </Badge>
            ) : (
              <span className="mat-summary-none">{t("MetricAnomalyTimeline.none.label")}</span>
            )}
          </div>
          <div className="mat-summary-label label-with-tip">
            {t("AnomalyDetection.highestSeverity.label")}
            <AnomalyInfoTip text={t("AnomalyDetection.highestSeverityToolTip.label")} />
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value">
            <Badge bg={HEALTH_BADGE[health] || "secondary"}>
              {t(healthLabelKey[health])}
            </Badge>
          </div>
          <div className="mat-summary-label">
            {t("MetricAnomalyTimeline.healthStatus.label")}
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value mat-summary-value-sm">
            {latest ? (
              <>
                <span>{formatValue(latest.value)}</span>
                {latest.hasAnomaly && (
                  <Badge bg={SEVERITY_BADGE[latest.maxSeverity] || "secondary"} className="ms-1">
                    {formatSeverityLabel(latest.maxSeverity, t)}
                  </Badge>
                )}
              </>
            ) : (
              "—"
            )}
          </div>
          <div className="mat-summary-label">
            {t("MetricAnomalyTimeline.latestPeriod.label")}
            {latest && <small className="d-block text-muted">{formatTimelinePeriod(latest.period)}</small>}
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value">{streaks.longestClean}</div>
          <div className="mat-summary-label">
            {t("MetricAnomalyTimeline.longestCleanStreak.label")}
          </div>
        </div>
      </div>
      <div className="col-6 col-md-4 col-xl">
        <div className="mat-summary-card">
          <div className="mat-summary-value">{streaks.longestUnhealthy}</div>
          <div className="mat-summary-label">
            {t("MetricAnomalyTimeline.longestUnhealthyStreak.label")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(SummaryStrip);
